import React, { useMemo, useState , useEffect} from 'react'
import {
  Card,
  Row,
  Col,
  Table,
  Select,
  DatePicker,
  Button,
  Statistic,
  Empty,
  Spin,
  Input,
} from 'antd'
import {
  ShoppingCartOutlined,
  ReloadOutlined,
  SearchOutlined,
  FileDoneOutlined,
  DollarOutlined,
} from '@ant-design/icons'
import BillService from '../../../../services/Billservice'
import { notify } from '../../../../components/Customs/NotifyProvider'
import { canViewModule, canCreateModule, canUpdateModule, canDeleteModule } from '../../../../utils/permissionUtils'

const { RangePicker } = DatePicker

const ParcelItemReport = () => {
  const MODULE_KEY = 'parcel_item_report'
  const canView = canViewModule(MODULE_KEY)
  const canCreate = canCreateModule(MODULE_KEY)
  const canUpdate = canUpdateModule(MODULE_KEY)
  const canDelete = canDeleteModule(MODULE_KEY)
  const isViewOnly = canView && !canCreate && !canUpdate && !canDelete
  const [loading, setLoading] = useState(false)
  const [rowsRaw, setRowsRaw] = useState([])
  const [dateFilter, setDateFilter] = useState('today')
  const [range, setRange] = useState(null)
  const [searchText, setSearchText] = useState('')

  const buildFilters = () => {
    if (isViewOnly) return { date: 'today' }
    const filters = {}

    if (dateFilter === 'custom' && range && range.length === 2) {
      filters.from = range[0].startOf('day').format('YYYY-MM-DD HH:mm:ss')
      filters.to = range[1].endOf('day').format('YYYY-MM-DD HH:mm:ss')
    } else if (dateFilter === 'today') {
      filters.date = 'today'
    } else if (dateFilter === 'yesterday') {
      filters.date = 'yesterday'
    }

    return filters
  }

  const fetchData = async () => {
    if (!canView) return
    setLoading(true)
    try {
      const filters = buildFilters()
      const response = await BillService.getParcelItemReport(filters)

      const rows = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
        ? response.data.data
        : []

      setRowsRaw(rows)
    } catch (err) {
      notify.error(`Failed to load parcel item report: ${err.message}`)
      setRowsRaw([])
    } finally {
      setLoading(false)
    }
  }


  //auto dispaly today
  useEffect(() => {
  fetchData()
}, [])

  const handleClear = () => {
    if (!canUpdate && !canDelete) {
      notify.error('Permission Denied', 'No permission to clear parcel item report filters')
      return
    }
    setDateFilter('today')
    setRange(null)
    setSearchText('')
    setRowsRaw([])
    
  }

  const rows = useMemo(() => {
    const search = searchText.trim().toLowerCase()

    return (rowsRaw || [])
      .map((row, index) => ({
        ...row,
        key: `${row.item_name}-${index}`,
        total_qty: Number(row.total_qty || 0),
        total_amount: Number(row.total_amount || 0),
        avg_price: Number(row.avg_price || 0),
        line_count: Number(row.line_count || 0),
      }))
      .filter((row) => {
        if (!search) return true
        return [
          row.item_name,
          String(row.total_qty),
          String(row.total_amount),
          String(row.avg_price),
          String(row.line_count),
        ]
          .join(' ')
          .toLowerCase()
          .includes(search)
      })
  }, [rowsRaw, searchText])

  const summary = useMemo(() => {
    return {
      totalItems: rows.length,
      totalQty: rows.reduce((sum, row) => sum + Number(row.total_qty || 0), 0),
      totalAmount: rows.reduce((sum, row) => sum + Number(row.total_amount || 0), 0),
    }
  }, [rows])

  const columns = [
    {
      title: 'Item Name',
      dataIndex: 'item_name',
      key: 'item_name',
      render: (value) => (
        <div>
          <div style={{ fontWeight: 700, color: '#0f172a' }}>{value || '-'}</div>
        </div>
      ),
    },
    {
      title: 'Lines',
      dataIndex: 'line_count',
      key: 'line_count',
      align: 'center',
      render: (value) => Number(value || 0),
    },
    {
      title: 'Total Qty',
      dataIndex: 'total_qty',
      key: 'total_qty',
      align: 'center',
      render: (value) => Number(value || 0),
    },
    {
      title: 'Avg Price',
      dataIndex: 'avg_price',
      key: 'avg_price',
      align: 'right',
      render: (value) => `₹${Number(value || 0).toFixed(2)}`,
    },
    {
      title: 'Total Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      align: 'right',
      render: (value) => (
        <strong style={{ color: '#0f172a' }}>
          ₹{Number(value || 0).toFixed(2)}
        </strong>
      ),
    },
  ]

  return !canView ? (
    <Card>
      <Empty description="No permission to view parcel item report" />
    </Card>
  ) : (
    <div className="table-report-page">
      <div className="table-report-hero">
        <div className="table-report-hero-left">
          <div className="table-report-hero-icon">
            <ShoppingCartOutlined />
          </div>
          <div>
            <h2 className="table-report-hero-title">Parcel Item Report</h2>
            <p className="table-report-hero-subtitle">
              Parcel item sales summary and quantity overview
            </p>
          </div>
        </div>
      </div>

      <Card className="table-report-card">
        <Row gutter={[12, 12]} className="table-report-filters">
          <Col xs={24} sm={12} md={8} lg={5}>
            <Select
              value={dateFilter}
              onChange={setDateFilter}
              disabled={isViewOnly}
              options={[
                { label: 'Today', value: 'today' },
                { label: 'Yesterday', value: 'yesterday' },
                { label: 'All', value: 'all' },
                { label: 'Custom Range', value: 'custom' },
              ]}
            />
          </Col>

          {dateFilter === 'custom' && !isViewOnly && (
            <Col xs={24} sm={12} md={10} lg={7}>
              <RangePicker
                style={{ width: '100%' }}
                value={range}
                onChange={(vals) => setRange(vals)}
              />
            </Col>
          )}

          <Col xs={24} sm={12} md={6} lg={4}>
            <Button
              type="primary"
              block
              icon={<ReloadOutlined />}
              className="table-report-load-btn"
              onClick={fetchData}
              loading={loading}
              disabled={!canView && !canCreate}
            >
              Load
            </Button>
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <Button
              block
              className="table-report-clear-btn"
              onClick={handleClear}
              disabled={!canUpdate && !canDelete}
            >
              Clear
            </Button>
          </Col>
        </Row>

        <div className="table-report-toolbar">
          <Input
            allowClear
            placeholder="Search parcel item..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="table-report-search"
          />
        </div>

        <Row gutter={[16, 16]} className="table-report-stats">
          <Col xs={24} sm={12} md={8}>
            <Card className="table-report-stat-card">
              <Statistic
                title="Items"
                value={summary.totalItems}
                prefix={<ShoppingCartOutlined />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Card className="table-report-stat-card">
              <Statistic
                title="Total Qty"
                value={summary.totalQty}
                prefix={<FileDoneOutlined />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Card className="table-report-stat-card">
              <Statistic
                title="Total Amount"
                value={summary.totalAmount}
                precision={2}
                prefix={<DollarOutlined />}
                formatter={(value) => `₹${Number(value || 0).toFixed(2)}`}
              />
            </Card>
          </Col>
        </Row>

        <Spin spinning={loading}>
          {!rows.length ? (
            <div className="table-report-empty">
              <Empty description="No parcel item report data found" />
            </div>
          ) : (
            <div className="table-report-modern-table">
              <Table
                rowKey="key"
                columns={columns}
                dataSource={rows}
                pagination={{ pageSize: 10 }}
              />
            </div>
          )}
        </Spin>
      </Card>
    </div>
  )
}

export default ParcelItemReport
